
import api from '@/apis/api'
import Coupon from '@/components/Coupon.vue'
import { Coupon as CouponModel } from '@/models/Coupon'
import {
  addDays,
  endOfDay,
  format,
  formatDuration,
  intervalToDuration,
  isAfter,
  isSameDay,
  isSameYear,
  parse,
  startOfDay,
} from 'date-fns'
import { uniq } from 'fp-ts/lib/Array'
import { pipe } from 'fp-ts/lib/function'
import Vue from 'vue'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { QrcodeStream } from 'vue-qrcode-reader'

@Component({
  components: { QrcodeStream, Coupon },
})
export default class Home extends Vue {
  dates = [this.today, this.today] as const
  datesDraft = [this.today, this.today] as const
  modal = false

  loading = true
  hasMore = false
  loadingMore = false
  coupons: CouponModel[] = []

  error: Error | undefined

  get formattedCoupons(): Record<string, unknown>[] {
    return this.coupons.map(x => ({
      ...x,
      redeemedOn: x.redeemedOn
        ? isSameDay(this.startDate, this.endDate)
          ? format(x.redeemedOn, 'h:mm aaaa')
          : isSameYear(this.startDate, this.endDate)
          ? format(x.redeemedOn, 'MM-dd h:mm aaaa')
          : format(x.redeemedOn, 'yyyy-MM-dd h:mm aaaa')
        : null,
      redemptionSubtotal: x.redemptionSubtotal ? x.redemptionSubtotal.toFixed(2) : null,
      redemptionDiscount: x.redemptionDiscount ? x.redemptionDiscount.toFixed(2) : null,
    }))
  }

  get startDate(): Date {
    return parse(this.dates[0], 'yyyy-MM-dd', new Date())
  }

  get endDate(): Date {
    return parse(this.dates[1], 'yyyy-MM-dd', new Date())
  }

  get today(): string {
    return format(new Date(), 'yyyy-MM-dd')
  }

  @Watch('modal')
  onModalChange(value: boolean): void {
    if (value) {
      this.datesDraft = this.dates
    }
  }

  get datesFormatted(): string {
    const min = parse(this.dates[0], 'yyyy-MM-dd', new Date())
    const max = parse(this.dates[1], 'yyyy-MM-dd', new Date())
    return this.dates[0] === this.dates[1]
      ? this.dates[0] === this.today
        ? `Today (${this.dates[0]})`
        : this.dates[0]
      : this.dates[1] === this.today
      ? `Last ${formatDuration(
          intervalToDuration({
            start: min,
            end: addDays(max, 1),
          }),
        )} (${this.dates[0]} to ${this.dates[1]})`
      : `${this.dates[0]} to ${this.dates[1]}`
  }

  async loadMore(): Promise<void> {
    this.loadingMore = true
    try {
      const min = this.startDate
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const max = this.coupons.slice(-1)[0].redeemedOn!
      const [coupons, hasMore] = await api.getRedemptionHistory(startOfDay(min), max)
      this.coupons = pipe([...this.coupons, ...coupons], uniq({ equals: (a, b) => a.id === b.id }))
      this.hasMore = hasMore
    } catch (e) {
      this.error = new Error(`${e}`)
    } finally {
      this.loadingMore = false
    }
  }

  @Watch('dates')
  async onDatesChange(value: readonly [string, string]): Promise<void> {
    this.loading = true
    try {
      this.coupons = []
      const min = parse(value[0], 'yyyy-MM-dd', new Date())
      const max = parse(value[1], 'yyyy-MM-dd', new Date())
      if (isAfter(min, max)) {
        this.dates = [this.dates[1], this.dates[0]]
        return
      }
      const [coupons, hasMore] = await api.getRedemptionHistory(startOfDay(min), endOfDay(max))
      this.coupons = coupons
      this.hasMore = hasMore
    } catch (e) {
      this.error = new Error(`${e}`)
    } finally {
      this.loading = false
    }
  }

  created(): void {
    this.onDatesChange(this.dates)
  }
}
